import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useDeepCompareEffect } from 'react-use'

import { candidateCandidatesList } from 'features/candidate/store'
import { NoDataTextMessage, OverlayDataShower, Pagination } from 'components/Elements'
import { Grid } from './Grid'
import { GeneralCandidatesFilter } from 'features/candidate/components/Candidates/Filters'
import { BILLING_SUBSCRIPTION_PERMISSION } from 'features/billing/consts/subscription'
import { useUserSubscription } from 'features/user/hooks'
import { BulkInviteControl } from 'features/job/components/JobActionControls'

const GeneralCandidatesContainerComponent = ({ ...props }) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { isPermissionAllow } = useUserSubscription()

  useEffect(() => {
    return () => {
      dispatch(candidateCandidatesList.resetDataState({}))
    }
  }, [])

  useDeepCompareEffect(() => {
    fetchList()
  }, [props.page, props.filters])

  const fetchList = () => {
    const { profile_certifications, ...restFilters } = props.filters

    let certificationsFilters = {}
    if (profile_certifications && profile_certifications.trim().length >= 2) {
      certificationsFilters = {
        profile_certifications: profile_certifications.trim(),
      }
    }

    let params = {
      page: props.page,
      filters: {
        ...restFilters,
        ...certificationsFilters,
      },
      sort: [{ id: 'candidate_score', desc: true }],
    }

    dispatch(candidateCandidatesList.getList({ params }))
  }

  const onPageChange = item => {
    dispatch(candidateCandidatesList.setPage(item.selected + 1))
  }

  const onFiltersChange = values => {
    dispatch(candidateCandidatesList.changeFilter({ ...values }))
  }

  const isLoading = useMemo(() => {
    return props.loading
  }, [props.loading])

  const inviteBlocked = !isPermissionAllow(BILLING_SUBSCRIPTION_PERMISSION.JOB_INVITE)

  const advancedFilter = isPermissionAllow(
    BILLING_SUBSCRIPTION_PERMISSION.CANDIDATES_ADVANCED_SEARCH_FILTER
  )

  const bulkInviteDisabled = useMemo(() => {
    return props.loading || !props.list.length
  }, [props.loading, !!props.list.length])

  return (
    <>
      <h4 className="font-size-18">{t('candidates')}</h4>

      <div className={'mb-2'}>
        <GeneralCandidatesFilter
          defaultValues={props.filters}
          onFiltersChange={onFiltersChange}
          advancedFilter={advancedFilter}
          renderRightSection={({ isApplied }) => (
            <BulkInviteControl
              blocked={inviteBlocked}
              disabled={bulkInviteDisabled || !isApplied}
              jobSelect
              onSuccess={fetchList}
              filters={props.filters}
            />
          )}
        />
      </div>

      <OverlayDataShower isLoading={isLoading} isFailed={!!props.error} error={props.error}>
        <Grid
          items={props.list}
          jobInviteControlProps={{ blocked: inviteBlocked, jobSelect: true }}
        />

        <Pagination data={props.meta} onPageChange={onPageChange} className={'mt-2'} />
      </OverlayDataShower>

      {!props.list.length && !props.loading && !props.error && (
        <NoDataTextMessage>{t('candidates_not_found')}</NoDataTextMessage>
      )}
    </>
  )
}

GeneralCandidatesContainerComponent.propTypes = {}

const mapStateToProps = state => {
  const { list, loading, error, meta, page, filters } = state.candidate.candidates.list
  return {
    list,
    loading,
    error,
    meta,
    page,
    filters,
  }
}

export const GeneralCandidatesContainer = connect(mapStateToProps)(
  GeneralCandidatesContainerComponent
)
